import qs from 'qs'
import { BaseQueryApi, BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'

import { getEnv } from '@/utils/helpers'
import {
  BaseParams,
  BaseResponse,
  CheckInsIdentityParams,
  CheckInsIdentityResponse,
  CreateTlsiConnectorParams,
  ErrorResponse,
  GetCardParams,
  GetCpxHpInfosResponse,
  GetInsFromIdentityInformationParams,
  GetInsFromRawVitaleDataParams,
  GetInsFromVitaleCardParams,
  GetInsiBatchValidationSetResultParams,
  GetInsiBatchValidationSetResultResponse,
  GetInsResponse,
  GetInteropCodesParams,
  GetInteropCodesResponse,
  OpenSessionParams,
  PcscReadersResponse,
  ReadCPxCardParams,
  ReadCPxCardResponse,
  ReadVitaleCardParams,
  ReadVitaleCardResponse,
  SessionExistsResponse,
  SessionResponse,
  SubmitInsiBatchValidationSetParams,
  SubmitInsiBatchValidationSetResponse,
  TlsiErrorResponse,
} from '../types/dmp-connect'
import { RootState } from '@/redux/rootReducer'
import {
  resetSession,
  selectDMPConnectOk,
  selectDMPConnectSessionId,
  setConnectorOk,
  setCpxCard,
  setCpxCardPracticeLocationIndex,
  setCpxCardReaderName,
  setDcParameter,
  setSessionId,
  setTlsiConnector,
} from '@/redux/reducers/dmp-connect.reducer'
import { encodePinCode } from '@/utils/dmp-connect.util'

const baseUrl = getEnv('VITE_DMPCONNECT_URL')

const baseQuery = fetchBaseQuery({
  baseUrl,
  paramsSerializer: (params) => qs.stringify(params, { allowEmptyArrays: true, arrayFormat: 'brackets' }),
})

const baseQueryWithErrorHandler: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions)
  if (result.error && result.error.status === 'FETCH_ERROR') {
    api.dispatch(setConnectorOk(false))
  }
  if (result.data && !selectDMPConnectOk(api.getState() as RootState)) {
    api.dispatch(setConnectorOk(true))
  }
  return result
}

export const dmpConnectJS2Api = createApi({
  reducerPath: 'dmpConnectJS2Api',
  baseQuery: baseQueryWithErrorHandler,
  endpoints: (builder) => ({
    // General
    getDcServiceVersion: builder.query<BaseResponse & { s_version: string }, void>({
      query: () => ({ url: '/remotecommand/getServiceVersion', method: 'POST', body: {} }),
    }),
    // Configuration
    registerDcParameter: builder.mutation<BaseResponse & { i_isProduction: number }, string>({
      query: (dcParameter) => ({ url: '/remotecommand/registerDcParameter', method: 'POST', body: { s_dcparameters64: dcParameter } }),
      async onQueryStarted(dcParameter, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          if (data.s_status === 'OK') {
            dispatch(setDcParameter(dcParameter))
            dispatch(
              dmpConnectJS2Api.util.updateQueryData('isDcParameterRegistered', dcParameter, (cached) => {
                Object.assign(cached, { i_registered: 1 })
              })
            )
          }
        } catch {}
      },
    }),
    isDcParameterRegistered: builder.query<BaseResponse & { i_registered: number }, string>({
      query: (dcParameter) => ({ url: '/remotecommand/isDcParameterRegistered', method: 'POST', body: { s_dcparameters64: dcParameter } }),
    }),
    // Session
    openSession: builder.mutation<SessionResponse, OpenSessionParams>({
      query: (body) => ({
        url: '/api/hl_openSession',
        method: 'POST',
        body: {
          s_commandName: 'hl_openSession',
          i_timeoutInSeconds: 3600,
          ...body,
        },
      }),
      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(setSessionId(data.s_sessionId))
          dispatch(setCpxCard(null))
          dispatch(setTlsiConnector(null))
        } catch {}
      },
    }),
    sessionExists: builder.query<SessionExistsResponse, string>({
      queryFn: (sessionId, api, extraOptions, baseQuery) => {
        const body: Record<string, any> = { s_commandName: 'hl_sessionExists', s_sessionId: sessionId }
        return sendCommand<SessionExistsResponse>(baseQuery, '/api/hl_sessionexists', body)
      },
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          if (data.i_exists === 0) {
            dispatch(resetSession())
          }
        } catch {
          dispatch(resetSession())
        }
      },
    }),
    // Cards
    getPcscReaders: builder.query<PcscReadersResponse, void>({
      queryFn: (arg, api, extraOptions, baseQuery) => {
        const body: Record<string, any> = { s_commandName: 'hl_getPcscReaders', s_sessionId: getSessionId(api) }
        return sendCommand<PcscReadersResponse>(baseQuery, '/api/hl_getpcscreaders', body)
      },
    }),
    getCpxHpInfos: builder.query<GetCpxHpInfosResponse, void>({
      queryFn: (arg, api, extraOptions, baseQuery) => {
        const body: Record<string, any> = { s_commandName: 'hl_getCpxHpInfos', s_sessionId: getSessionId(api) }
        return sendCommand<GetCpxHpInfosResponse>(baseQuery, '/api/hl_getcpxhpinfos', body)
      },
    }),
    selectCPxCard: builder.mutation<BaseResponse, GetCardParams & { saveCard?: boolean }>({
      queryFn: (arg, api, extraOptions, baseQuery) => {
        const body: Record<string, any> = { ...arg, s_commandName: 'hl_getCpxCard', s_sessionId: getSessionId(api) }
        return sendCommand<BaseResponse>(baseQuery, '/api/hl_getcpxcard', body)
      },
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          if (data.s_status !== 'OK') return
          if ('s_readerName' in params && params.saveCard) dispatch(setCpxCardReaderName(params.s_readerName))
        } catch {}
      },
    }),
    readCPxCard: builder.query<ReadCPxCardResponse, ReadCPxCardParams>({
      queryFn: (arg, api, extraOptions, baseQuery) => {
        const body: BaseParams & ReadCPxCardParams = { i_returnCertificates: 0, ...arg, s_commandName: 'hl_readCpxCard', s_sessionId: getSessionId(api) }
        return sendCommand<ReadCPxCardResponse>(baseQuery, '/api/hl_readcpxcard', body)
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          const encodedPinCode = encodePinCode(arg.s_pinCode)
          dispatch(setCpxCard({ ...data, encodedPinCode }))
        } catch {}
      },
    }),
    selectVitaleCard: builder.mutation<BaseResponse, GetCardParams>({
      queryFn: (arg, api, extraOptions, baseQuery) => {
        const body: Record<string, any> = { ...arg, s_commandName: 'hl_getVitaleCard', s_sessionId: getSessionId(api) }
        return sendCommand<BaseResponse>(baseQuery, '/api/hl_getvitalecard', body)
      },
    }),
    readVitaleCard: builder.query<ReadVitaleCardResponse, ReadVitaleCardParams>({
      queryFn: ({ i_getXmlContent = 0 }, api, extraOptions, baseQuery) => {
        const body: BaseParams & ReadVitaleCardParams = { i_getXmlContent, s_commandName: 'hl_readVitaleCard', s_sessionId: getSessionId(api) }
        return sendCommand<ReadVitaleCardResponse>(baseQuery, '/api/hl_readvitalecard', body)
      },
    }),
    // tlsi
    createTlsiConnector: builder.mutation<BaseResponse & { s_instanceUniqueId: string }, CreateTlsiConnectorParams & { billingNumber: string }>({
      queryFn: async ({ billingNumber, ...arg }, api, extraOptions, baseQuery) => {
        const body: BaseParams & CreateTlsiConnectorParams = {
          i_transactionsTimeout: 60,
          s_tlsiUrl: 'https://qualiflps.services-ps.ameli.fr',
          ...arg,
          s_commandName: 'hl_createTlsiConnector',
          s_sessionId: getSessionId(api),
        }
        const result = await sendCommand<BaseResponse & { s_instanceUniqueId: string }>(baseQuery, '/api/hl_createTlsiConnector', body)
        if (result.data) {
          result.data.s_instanceUniqueId = await generateUniqueId(getSessionId(api))
        }
        return result
      },
      async onQueryStarted({ billingNumber, i_cpsPracticeLocationIndice, s_practiceSetting }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(setTlsiConnector({ billingNumber, instanceUniqueId: data.s_instanceUniqueId, practiceSetting: s_practiceSetting }))
          dispatch(setCpxCardPracticeLocationIndex(i_cpsPracticeLocationIndice))
        } catch {}
      },
    }),
    // ins
    getInsFromVitaleCard: builder.query<GetInsResponse, GetInsFromVitaleCardParams>({
      queryFn: (arg, api, extraOptions, baseQuery) => {
        const body: BaseParams & GetInsFromVitaleCardParams = {
          ...arg,
          s_commandName: 'hl_getInsFromVitaleCard',
          s_sessionId: getSessionId(api),
        }
        return sendCommand<GetInsResponse>(baseQuery, '/api/hl_getinsfromvitalecard', body)
      },
    }),
    getInsFromRawVitaleData: builder.query<GetInsResponse, GetInsFromRawVitaleDataParams>({
      queryFn: (arg, api, extraOptions, baseQuery) => {
        const body: BaseParams & GetInsFromRawVitaleDataParams = {
          ...arg,
          s_commandName: 'hl_getInsFromRawVitaleData',
          s_sessionId: getSessionId(api),
        }
        return sendCommand<GetInsResponse>(baseQuery, '/api/hl_getinsfromrawvitaledata', body)
      },
    }),
    getInsFromIdentityInformation: builder.query<GetInsResponse, GetInsFromIdentityInformationParams>({
      queryFn: (arg, api, extraOptions, baseQuery) => {
        const body: BaseParams & GetInsFromIdentityInformationParams = {
          ...arg,
          s_commandName: 'hl_getInsFromIdentityInformation',
          s_sessionId: getSessionId(api),
        }
        return sendCommand<GetInsResponse>(baseQuery, '/api/hl_getinsfromidentityinformation', body)
      },
    }),
    checkInsIdentity: builder.query<CheckInsIdentityResponse, CheckInsIdentityParams>({
      queryFn: (arg, api, extraOptions, baseQuery) => {
        const body: BaseParams & CheckInsIdentityParams = {
          ...arg,
          i_getTransactionFrames: 0,
          s_commandName: 'hl_checkInsIdentity',
          s_sessionId: getSessionId(api),
        }
        return sendCommand<CheckInsIdentityResponse>(baseQuery, '/api/hl_checkinsidentity', body)
      },
    }),
    submitInsiBatchValidationSet: builder.mutation<SubmitInsiBatchValidationSetResponse, SubmitInsiBatchValidationSetParams>({
      queryFn: (arg, api, extraOptions, baseQuery) => {
        const body: BaseParams & SubmitInsiBatchValidationSetParams = {
          ...arg,
          s_commandName: 'hl_submitInsiBatchValidationSet',
          s_sessionId: getSessionId(api),
        }
        return sendCommand<SubmitInsiBatchValidationSetResponse>(baseQuery, '/api/hl_submitinsibatchvalidationset', body)
      },
    }),
    getInsiBatchValidationSetResult: builder.query<GetInsiBatchValidationSetResultResponse, GetInsiBatchValidationSetResultParams>({
      queryFn: (arg, api, extraOptions, baseQuery) => {
        const body: BaseParams & GetInsiBatchValidationSetResultParams = {
          ...arg,
          s_commandName: 'hl_getInsiBatchValidationSetResult',
          s_sessionId: getSessionId(api),
        }
        return sendCommand<GetInsiBatchValidationSetResultResponse>(baseQuery, '/api/hl_getinsibatchvalidationsetresult', body)
      },
    }),
    // DMP
    getInteropCodes: builder.query<GetInteropCodesResponse, GetInteropCodesParams>({
      queryFn: (arg, api, extraOptions, baseQuery) => {
        const body: BaseParams & GetInteropCodesParams = {
          ...arg,
          s_commandName: 'hl_getInteropCodes',
          s_sessionId: getSessionId(api),
        }
        return sendCommand<GetInteropCodesResponse>(baseQuery, '/api/hl_getinteropcodes', body)
      },
    }),
  }),
})

function getSessionId(api: BaseQueryApi) {
  return selectDMPConnectSessionId(api.getState() as RootState)
}

async function sendCommand<T extends object>(
  baseQuery: (args: string | FetchArgs) => ReturnType<BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>>,
  url: string,
  body: Record<string, any>
) {
  const result = await baseQuery({ url, method: 'POST', body })
  if (result.data) {
    const data = result.data as T
    if ('s_status' in data && data.s_status === 'ERROR') {
      const error =
        's_apiErrorTlsiErrorDetailCode' in data
          ? (data as unknown as TlsiErrorResponse).s_apiErrorTlsiErrorDetailError
          : (data as unknown as ErrorResponse).s_apiErrorExtendedInformations
      return { error: { status: 'CUSTOM_ERROR', error } as FetchBaseQueryError }
    }
    return { data }
  }
  return { error: result.error as FetchBaseQueryError }
}

async function generateUniqueId(sessionId?: string) {
  const response = await fetch(`${baseUrl}/api/hl_generateUniqueId`, {
    method: 'POST',
    body: JSON.stringify({ s_commandName: 'hl_generateUniqueId', i_type: 2, s_sessionId: sessionId }),
    headers: {
      Content: 'application/json',
    },
  })
  if (!response.ok) {
    throw new Error('Failed to generate UUID')
  }
  const { s_uniqueId } = (await response.json()) as BaseResponse & { s_uniqueId: string }
  return s_uniqueId
}
